/**
 * THE MOTION VOCABULARY.
 *
 * Before this module every screen invented its own animation: a CSS class
 * toggle here, a `setTimeout` there, and no two popups agreeing on a duration.
 * The owner asked for "animation of everything, buttons, places, lines" — which
 * only reads as one game if the timings come from one place.
 *
 * ── WHERE THE VOCABULARY LIVES ──────────────────────────────────────────────
 * This file owns the motion a caller RUNS (`enterScreen`, `enterRow`, `bump`,
 * `attention`, `idle`). The motion a control PERFORMS BY ITSELF — press, hover,
 * disabled, focus — is CSS, and lives in `press.ts` and `controls.ts`, composed
 * out of the `--rga-press-*` custom properties documented at the top of
 * `PRESS_CSS`. Keeping the two apart is deliberate: a control must not need a
 * JavaScript call to acknowledge a finger.
 *
 * ── THE MEASURED DECISIONS (docs/UI-INTERACTIONS.md) ────────────────────────
 * A modal APPEARS. The reference captures resolve a panel in ONE frame, under
 * 33 ms — so `enterPopup` deliberately has NO entrance animation. It exists so
 * that a screen reaching for "a nice 200 ms fade" finds a function that says,
 * in code, that the fade is wrong. Motion belongs to the DISMISSAL, to list
 * rows, to counters, and to rewards.
 *
 * ── WHY THE WEB ANIMATIONS API ──────────────────────────────────────────────
 * A CSS class toggle has no reliable completion callback: `transitionend` never
 * fires when the property does not actually change, when the node is detached,
 * or when the tab is backgrounded. Every helper here therefore promises the
 * same contract, and it is the contract every caller depends on:
 *
 *   THE COMPLETION CALLBACK FIRES EXACTLY ONCE, AND THE END STATE IS ALWAYS
 *   APPLIED — on natural finish, on cancel, under `prefers-reduced-motion`,
 *   and in an environment with no `Element.prototype.animate` at all (jsdom).
 *
 * That last case is not hypothetical: the test environment has neither
 * `Element.prototype.animate` nor `matchMedia`, so the fallback path is the one
 * the suite exercises. `animateWith` is the seam that lets a test drive the
 * real WAAPI branch with a fake.
 */
import { systemPrefersReducedMotion } from '../a11y/motion';

/** Anything we need of an `Animation`. Keeps the fake in tests honest. */
export interface MotionAnimation {
  cancel(): void;
  onfinish: ((this: unknown, ev: unknown) => unknown) | null;
  oncancel?: ((this: unknown, ev: unknown) => unknown) | null;
}

/** The `Element.prototype.animate` shape, injectable for tests. */
export type AnimateFn = (
  node: HTMLElement,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
) => MotionAnimation;

export interface MotionOptions {
  /** Force the reduced-motion branch on or off. Defaults to the OS setting. */
  readonly reducedMotion?: boolean;
  /** Fires EXACTLY ONCE, after the end state has been applied. */
  readonly onDone?: () => void;
  /** Test seam. Defaults to `Element.prototype.animate` when it exists. */
  readonly animateWith?: AnimateFn;
}

/** A running (or already-finished) motion. `cancel` still lands the end state. */
export interface Motion {
  cancel(): void;
  readonly finished: Promise<void>;
  readonly done: boolean;
}

/** The one place the reduced-motion question is asked. */
export function motionReduced(options: MotionOptions = {}): boolean {
  if (typeof options.reducedMotion === 'boolean') return options.reducedMotion;
  return systemPrefersReducedMotion();
}

function nativeAnimate(): AnimateFn | null {
  const proto = globalThis.Element?.prototype as { animate?: unknown } | undefined;
  if (typeof proto?.animate !== 'function') return null;
  return (node, keyframes, opts) =>
    (node as unknown as { animate: (k: Keyframe[], o: KeyframeAnimationOptions) => MotionAnimation })
      .animate(keyframes, opts);
}

/** Write a keyframe's properties onto the node as inline styles. */
function applyFrame(node: HTMLElement, frame: Keyframe | undefined): void {
  if (frame === undefined) return;
  for (const [key, value] of Object.entries(frame)) {
    if (key === 'offset' || key === 'easing' || key === 'composite') continue;
    if (value === undefined || value === null) continue;
    node.style.setProperty(
      key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`),
      String(value),
    );
  }
}

/**
 * Run one animation under the completion contract above.
 *
 * `keyframes` must END on the state you want to persist: the last frame is
 * written as inline style when the motion resolves, so a cancelled or skipped
 * animation leaves the node exactly where a finished one would.
 */
export function runMotion(
  node: HTMLElement,
  keyframes: Keyframe[],
  timing: KeyframeAnimationOptions,
  options: MotionOptions = {},
): Motion {
  let settled = false;
  let anim: MotionAnimation | null = null;
  let resolveFinished: () => void = () => {};
  const finished = new Promise<void>((resolve) => {
    resolveFinished = resolve;
  });

  const settle = (): void => {
    if (settled) return;
    settled = true;
    applyFrame(node, keyframes[keyframes.length - 1]);
    options.onDone?.();
    resolveFinished();
  };

  const animate = options.animateWith ?? nativeAnimate();
  if (animate === null || motionReduced(options) || keyframes.length === 0) {
    settle();
    return { cancel: settle, finished, done: true };
  }

  try {
    anim = animate(node, keyframes, timing);
    anim.onfinish = settle;
    if ('oncancel' in anim) anim.oncancel = settle;
  } catch {
    // A node detached mid-call, or an engine that rejects these keyframes.
    settle();
  }

  return {
    cancel: (): void => {
      try {
        anim?.cancel();
      } catch {
        /* already gone */
      }
      settle();
    },
    finished,
    get done(): boolean {
      return settled;
    },
  };
}

/**
 * A popup APPEARS. No entrance animation — see the header. This is a function
 * rather than nothing so that call sites have somewhere to put the intent, and
 * so the decision has one place to change if the reference is ever re-measured.
 */
export function enterPopup(node: HTMLElement, options: MotionOptions = {}): Motion {
  node.style.removeProperty('opacity');
  node.style.removeProperty('transform');
  return runMotion(node, [{ opacity: '1' }], { duration: 0 }, { ...options, reducedMotion: true });
}

/** The dismissal, which the reference DOES animate. ~120 ms out. */
export function exitPopup(node: HTMLElement, options: MotionOptions = {}): Motion {
  return runMotion(
    node,
    [
      { opacity: '1', transform: 'scale(1)' },
      { opacity: '0', transform: 'scale(.94)' },
    ],
    { duration: 120, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
    options,
  );
}

/** Total stagger budget: a 40-row list must not take a second to arrive. */
export const ROW_STAGGER_MS = 22;
export const ROW_STAGGER_CAP_MS = 240;

/** A list row sliding in. `index` drives the (clamped) stagger. */
export function enterRow(node: HTMLElement, index = 0, options: MotionOptions = {}): Motion {
  const delay = Math.min(index * ROW_STAGGER_MS, ROW_STAGGER_CAP_MS);
  return runMotion(
    node,
    [
      { opacity: '0', transform: 'translateY(8px)' },
      { opacity: '1', transform: 'translateY(0)' },
    ],
    { duration: 160, delay, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'both' },
    options,
  );
}

/** A row leaving. */
export function exitRow(node: HTMLElement, index = 0, options: MotionOptions = {}): Motion {
  const delay = Math.min(index * ROW_STAGGER_MS, ROW_STAGGER_CAP_MS);
  return runMotion(
    node,
    [
      { opacity: '1', transform: 'translateY(0)' },
      { opacity: '0', transform: 'translateY(6px)' },
    ],
    { duration: 130, delay, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
    options,
  );
}

/**
 * THE COUNTER POP. A number that changes without moving is a number nobody
 * notices; this is what `flyTo` fires when a reward lands on its header chip.
 * The end frame is `scale(1)`, so an interrupted bump leaves nothing shrunk.
 */
export function bump(node: HTMLElement, options: MotionOptions = {}): Motion {
  return runMotion(
    node,
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.18)', offset: 0.4 },
      { transform: 'scale(1)' },
    ],
    { duration: 260, easing: 'cubic-bezier(.34,1.56,.64,1)' },
    options,
  );
}

/**
 * THE SCREEN CHANGE (`VISUAL-VOLUME-AND-MOTION.md` §3.4).
 *
 * A meta navigation rebuilds the shell and the new screen simply exists. That
 * is not merely undramatic, it is AMBIGUOUS: the header and the tab bar are
 * identical between screens, so on a slow phone nothing on the page confirms
 * the tap landed. 160 ms of the content host arriving is that confirmation.
 *
 * Three details that are not decoration:
 *
 *  · ONLY THE CONTENT HOST MOVES. The header and the tab bar are the anchor. If
 *    they move too the transition reads as the whole page reloading.
 *  · 6 px, NOT 16. The reference is a lobby that resolves in one frame, and a
 *    long slide is the wrong register beside a modal that does not animate at
 *    all (`enterPopup`, above).
 *  · THE TRANSFORM IS STRIPPED WHEN IT FINISHES, AND THE FILL IS `backwards`.
 *    A live `transform` on an ancestor — even `translateY(0)`, which is not
 *    `none` — makes that element the containing block for every
 *    `position:fixed` descendant. Nothing breaks today (`createBubble` appends
 *    to `document.body`, and `.rga-modal` / `.rga-fly-layer` are body-level)
 *    but leaving one on the node every screen mounts into is a trap for
 *    whoever next writes a fixed overlay.
 *    BOTH halves are needed and only together. `onDone` removes the inline
 *    style on every path — natural finish, cancel, reduced motion, no WAAPI —
 *    because `runMotion` guarantees it fires exactly once. That alone would not
 *    be enough: `fill:'forwards'`/`'both'` keeps a FINISHED animation applying
 *    its last keyframe from the animation stack, which outranks inline styles,
 *    so the transform would survive its own removal. `backwards` fills only the
 *    delay before the start (guarding the one-frame flash of the end state) and
 *    stops applying the moment the animation ends.
 */
export const SCREEN_ENTER_MS = 160;

export function enterScreen(host: HTMLElement, options: MotionOptions = {}): Motion {
  return runMotion(
    host,
    [
      { opacity: '0', transform: 'translateY(6px)' },
      { opacity: '1', transform: 'translateY(0)' },
    ],
    { duration: SCREEN_ENTER_MS, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'backwards' },
    {
      ...options,
      onDone: () => {
        host.style.removeProperty('transform');
        options.onDone?.();
      },
    },
  );
}

/**
 * THE ATTENTION BEAT — one shot, on arrival, and then it stops.
 *
 * `VISUAL-VOLUME-AND-MOTION.md` §3.3 wants the eye pulled to the screen's one
 * call to action (Battle's "Enter the arena") and explicitly DECLINES to give
 * it an idle pulse: `docs/UI-STYLE.md` reserves idle motion for rewards, and
 * `idle()` above refuses a non-reward node for that reason. This is the
 * sanctioned alternative — a response to arrival rather than a loop, so it
 * cannot compete with a reward for the eye a second later.
 *
 * 220 ms and 1.04, deliberately weaker than `bump`'s 260 ms and 1.18: a counter
 * that changed has news, a button that is merely present does not. The end
 * frame is `scale(1)` so an interrupted beat leaves nothing enlarged, and under
 * reduced motion the whole thing collapses to that end frame — a non-essential
 * attention-getter is exactly what `prefers-reduced-motion` asks to lose.
 *
 * THE INLINE TRANSFORM IS REMOVED WHEN IT FINISHES, and that line is the whole
 * reason this is not a two-line call to `bump`. This beat lands on a CTA, and a
 * CTA is a `.rga-plate` — whose press cue is a `transform` composed from
 * `--rga-press-y` / `--rga-press-s` in the stylesheet. An inline
 * `transform:scale(1)` beats any stylesheet rule, so a button that had been
 * greeted would have gone permanently dead to the press for the life of the
 * screen: still clickable, no longer sinking, and invisible to jsdom. `bump`
 * keeps its inline end state because its subjects are header counters that own
 * no CSS transform of their own.
 */
export const ATTENTION_MS = 220;

export function attention(node: HTMLElement, options: MotionOptions = {}): Motion {
  return runMotion(
    node,
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.04)', offset: 0.45 },
      { transform: 'scale(1)' },
    ],
    { duration: ATTENTION_MS, easing: 'cubic-bezier(.34,1.56,.64,1)' },
    {
      ...options,
      onDone: () => {
        node.style.removeProperty('transform');
        options.onDone?.();
      },
    },
  );
}

/** The idle repertoire. Rewards only — see `idle`. */
export type IdleKind = 'bob' | 'turn' | 'shimmer';

const IDLE_MARK = 'motion-idle-css';
const IDLE_CSS = `
@keyframes rga-idle-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@keyframes rga-idle-turn{0%,100%{transform:rotate(-4deg)}50%{transform:rotate(4deg)}}
@keyframes rga-idle-shimmer{0%,100%{filter:brightness(1)}50%{filter:brightness(1.22)}}
.rga-idle[data-idle=bob]{animation:rga-idle-bob 1.6s ease-in-out infinite}
.rga-idle[data-idle=turn]{animation:rga-idle-turn 2.4s ease-in-out infinite}
.rga-idle[data-idle=shimmer]{animation:rga-idle-shimmer 1.8s ease-in-out infinite}
@media (prefers-reduced-motion: reduce){.rga-idle{animation:none!important}}
`;

/** Inject the idle keyframes once per document. */
export function ensureMotionStyles(doc: Document = document): void {
  if (doc.querySelector(`style[data-rga="${IDLE_MARK}"]`) !== null) return;
  const style = doc.createElement('style');
  style.setAttribute('data-rga', IDLE_MARK);
  style.textContent = IDLE_CSS;
  (doc.head ?? doc.documentElement).append(style);
}

export interface IdleOptions extends MotionOptions {
  /**
   * Idle motion is for REWARDS ONLY (`docs/UI-STYLE.md`: "idle motion on
   * REWARDS ONLY, static battle background"). A node outside a reward subtree
   * is refused unless this says the caller knows better.
   */
  readonly force?: boolean;
}

/** Is this node a legitimate subject for idle motion? */
export function isRewardSubject(node: HTMLElement): boolean {
  return node.closest('[data-reward],.rga-rw,.rga-chest') !== null;
}

/**
 * Bob / turn / shimmer, forever, until the returned stop() is called.
 *
 * OFF BY DEFAULT everywhere: nothing idles unless a call site asks, and a call
 * site outside a reward subtree is refused (a warning and a no-op, never a
 * throw — a decorative animation must not be able to take down a screen).
 */
export function idle(node: HTMLElement, kind: IdleKind, options: IdleOptions = {}): () => void {
  if (options.force !== true && !isRewardSubject(node)) {
    console.warn(`[motion] idle('${kind}') refused: not a reward element`, node);
    return () => {};
  }
  if (motionReduced(options)) return () => {};
  ensureMotionStyles(node.ownerDocument);
  node.classList.add('rga-idle');
  node.dataset.idle = kind;
  return (): void => {
    node.classList.remove('rga-idle');
    delete node.dataset.idle;
  };
}
